import React, { useState } from 'react';
import { Send, CheckCircle2, AlertCircle, Loader2 } from 'lucide-react';
import { siteConfig } from '../data';

export default function ContactForm({ defaultSubject = "" }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    subject: defaultSubject || 'Forespørgsel om coaching',
    message: '',
    website_url: '', // Honeypot field
  });

  const [status, setStatus] = useState('idle'); // 'idle' | 'submitting' | 'success' | 'error'
  const [errorMessage, setErrorMessage] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus('submitting');
    setErrorMessage('');

    try {
      const response = await fetch('https://api.dev.auxo.dk/webform/submit', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (response.ok) {
        setStatus('success');
        setFormData({
          name: '',
          email: '',
          phone: '',
          subject: defaultSubject || 'Forespørgsel om coaching',
          message: '',
          website_url: '',
        });
      } else {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData.message || 'Der skete en fejl under afsendelsen. Prøv venligst igen.');
      }
    } catch (error) {
      console.error('Form submission error:', error);
      setStatus('error');
      setErrorMessage(error.message || 'Kunne ikke oprette forbindelse til serveren. Prøv igen senere.');
    }
  };

  return (
    <div className="rounded-none border border-slate-100 bg-white p-6 shadow-xl shadow-slate-100/50 sm:p-8">
      {status === 'success' ? (
        <div className="text-center py-8">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-none bg-emerald-50 text-emerald-500">
            <CheckCircle2 className="h-8 w-8" />
          </div>
          <h3 className="text-xl font-bold text-blue-950">Besked sendt!</h3>
          <p className="mt-2 text-sm text-slate-600 leading-relaxed">
            Tak for din henvendelse, Steffen vil vende tilbage til dig hurtigst muligt på e-mail eller telefon.
          </p>
          <button
            onClick={() => setStatus('idle')}
            className="mt-6 rounded-none bg-blue-950 px-6 py-2.5 text-sm font-semibold text-white hover:bg-blue-900 hover:-translate-y-1 active:scale-98 transition-all duration-300"
          >
            Send en ny besked
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Honeypot Field - ABSOLUTELY HIDDEN from humans */}
          <div className="sr-only absolute -z-10 overflow-hidden h-0 w-0" aria-hidden="true">
            <label htmlFor="website_url">Hjemmeside URL (Udfyld ikke denne)</label>
            <input
              type="text"
              id="website_url"
              name="website_url"
              value={formData.website_url}
              onChange={handleChange}
              tabIndex="-1"
              autoComplete="off"
            />
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="name" className="block text-sm font-medium text-slate-700 mb-1.5">
                Navn <span className="text-sky-500">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                required
                value={formData.name}
                onChange={handleChange}
                placeholder="Dit fulde navn"
                className="w-full rounded-none border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500 transition-all"
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-slate-700 mb-1.5">
                E-mail <span className="text-sky-500">*</span>
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                value={formData.email}
                onChange={handleChange}
                placeholder="din@email.dk"
                className="w-full rounded-none border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500 transition-all"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <label htmlFor="phone" className="block text-sm font-medium text-slate-700 mb-1.5">
                Telefonnummer
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="F.eks. 41174266"
                className="w-full rounded-none border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500 transition-all"
              />
            </div>

            <div>
              <label htmlFor="subject" className="block text-sm font-medium text-slate-700 mb-1.5">
                Emne <span className="text-sky-500">*</span>
              </label>
              <select
                id="subject"
                name="subject"
                required
                value={formData.subject}
                onChange={handleChange}
                className="w-full rounded-none border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500 transition-all"
              >
                <option value="Forespørgsel om coaching">Forespørgsel om coaching</option>
                <option value="Mindsetforløb">Mindsetforløb</option>
                <option value="Kostvejledning (Kommende)">Kostvejledning (Kommende)</option>
                <option value="Personlig træning (Kommende)">Personlig træning (Kommende)</option>
                <option value="Andet">Andet</option>
              </select>
            </div>
          </div>

          <div>
            <label htmlFor="message" className="block text-sm font-medium text-slate-700 mb-1.5">
              Besked <span className="text-sky-500">*</span>
            </label>
            <textarea
              id="message"
              name="message"
              required
              rows="4"
              value={formData.message}
              onChange={handleChange}
              placeholder="Skriv lidt om dig selv, og hvad du ønsker at arbejde med..."
              className="w-full rounded-none border border-slate-200 bg-slate-50/50 px-4 py-3 text-sm text-slate-900 placeholder-slate-400 focus:border-sky-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-sky-500 transition-all resize-none"
            ></textarea>
          </div>

          {status === 'error' && (
            <div className="flex items-center gap-2.5 rounded-none bg-red-50 p-4 text-sm text-red-700">
              <AlertCircle className="h-5 w-5 shrink-0" />
              <p>{errorMessage}</p>
            </div>
          )}

          <button
            type="submit"
            disabled={status === 'submitting'}
            className="flex w-full items-center justify-center gap-2 rounded-none bg-blue-950 py-3.5 text-sm font-semibold text-white hover:bg-blue-900 hover:-translate-y-1 active:scale-98 disabled:opacity-70 disabled:cursor-not-allowed transition-all duration-300"
          >
            {status === 'submitting' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Sender besked...
              </>
            ) : (
              <>
                <Send className="h-4 w-4" />
                Send besked
              </>
            )}
          </button>
        </form>
      )}
    </div>
  );
}
